import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { CSVLink } from 'react-csv';
import { isWidthDown } from '@material-ui/core/withWidth';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import PCB_TYPE from '../../images/pcb_type.svg';

import Skeleton from '@material-ui/lab/Skeleton';

import SaveIcon from '@material-ui/icons/SaveAltSharp';

import pushToGTM, { EXPORT_OPERATING_VALUES_GA4_TAG } from '../../utils/analytics/gtm';
import { DIMENSIONS, NUMBER_OF_SKELETON_TABLE_ROWS } from '../../styles/constants';
import { OP_VALUES_CSV_HEADER, OP_VALUES_TABLE_COLS } from './constants';
import { buildChipsData, buildVinNote, selectOperatingValues, buildSelectItems, cleanupHtmlFromString } from './utils';
import Table from '../Table';
import Select from '../Select';
import Chips from '../Chips';
import TableSkeleton from '../Table/TableSkeleton';
import { useTranslation } from 'react-i18next';
import BlurWrapper from '../BlurWrapper';

const OperatingValues = ({ opValuesCategory, setOpValuesCategory, withTitle, screenWidth, logIn }) => {
	const { t } = useTranslation();
	const opValueCategories = useSelector(state => state.selectedDesign.detail.opValueCategories);
	const topology = useSelector(state => state.selectedDesign.topology);
	const topologyCategory = useSelector(state => state.selectedDesign.detail.rankingParameters.topologyCategory);
	const icPartNumber = useSelector(state => state.selectedDesign.icPartNumber);
	const designId = useSelector(state => state.selectedDesign.myDesign.id);
	const inputParametersSD = useSelector(state => state.selectedDesign.detail.inputParameters);
	const initialVinNom = useSelector(state => state.selectedDesign.initialInputParameters.vinNom);
	const progressActive = useSelector(state => state.tabbar.progressActive);
	const userLoggedIn = useSelector(state => state.user.loggedIn);

	const selectCategory = event => {
		setOpValuesCategory(event.target.value);
	};

	const toggleCategory = category => {
		if (category !== opValuesCategory) {
			setOpValuesCategory(category);
		}
	};

	const prepareCsvData = data => {
		return data.map(d => ({
			...d,
			label: cleanupHtmlFromString(d.label)
		}));
	};

	return progressActive ? (
		<TableSkeleton numberOfRows={NUMBER_OF_SKELETON_TABLE_ROWS} />
	) : (
		<Table
			id="OperatingValues__table"
			title={withTitle && t('components.opValues.title')}
			actions={
				<BlurWrapper isBlurred={!userLoggedIn} showLoginButton={false}>
					<Grid container justify={'flex-end'} alignItems="center" spacing={2}>
						<Grid item xs>
							{isWidthDown('xs', screenWidth) ? (
								<Select
									value={opValuesCategory}
									onChange={selectCategory}
									items={buildSelectItems(opValueCategories)}
								/>
							) : (
								<Chips
									value={opValuesCategory}
									updateValue={toggleCategory}
									chips={buildChipsData(opValueCategories)}
								/>
							)}
						</Grid>

						<Grid item>
							<Tooltip title={t('components.opValues.downloadCSV')}>
								<CSVLink
									data={prepareCsvData(selectOperatingValues(opValueCategories, 'ALL'))}
									headers={OP_VALUES_CSV_HEADER}
									filename={'WebDesigner+ ' + designId + ' - ' + icPartNumber + ' OPvalues.csv'}>
									<IconButton
										aria-label="Download CSV"
										onClick={() => {
											pushToGTM(EXPORT_OPERATING_VALUES_GA4_TAG);
										}}>
										<SaveIcon />
									</IconButton>
								</CSVLink>
							</Tooltip>
						</Grid>
					</Grid>
				</BlurWrapper>
			}
			footerContent={
				progressActive ? (
					<Skeleton height={DIMENSIONS.SKELETON_TEXT_HEIGHT} />
				) : (
					<>
						<BlurWrapper isBlurred={!userLoggedIn} showLoginButton={false}>
							<Typography variant="caption" color="textSecondary">
								{buildVinNote(inputParametersSD, initialVinNom, topology, topologyCategory)}
							</Typography>
							{opValuesCategory === 'THERMAL' && (
								<img
									style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto', marginTop: '6px' }}
									src={PCB_TYPE}></img>
							)}
						</BlurWrapper>
					</>
				)
			}
			columns={OP_VALUES_TABLE_COLS}
			items={opValueCategories.length !== 0 ? selectOperatingValues(opValueCategories, opValuesCategory) : []}
			logIn={logIn}
		/>
	);
};

OperatingValues.propTypes = {
	opValuesCategory: PropTypes.string.isRequired,
	setOpValuesCategory: PropTypes.func.isRequired,
	withTitle: PropTypes.bool,
	screenWidth: PropTypes.number.isRequired
};

export default OperatingValues;
